import React, {useState, useEffect, useRef, memo, useLayoutEffect} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {Spin} from 'antd';

import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';

import {Home, Bookmarks, Settings, PersonOutline, DarkModeOutlined, LightModeOutlined} from '@mui/icons-material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {LogoutOutlined, LockOutlined, UserOutlined} from '@ant-design/icons';

import useMenuControl from '../hooks/useMenuControl';
import useAuthData from '../hooks/useAuthData';
import ChangePasswordModal from './modal/EditarSenha';
import EditarCredencial from './modal/EditarCredencial';
import {useTheme} from '../context/ThemeContext';
import useUserType from "../hooks/useUserType";
import {useDashboards} from "../hooks/useDashboards";
import {jwtDecode} from "jwt-decode";


function getDropdownDirection(btnRef, menuHeight = 220) {
    if (!btnRef.current) return 'down';
    const btnRect = btnRef.current.getBoundingClientRect();
    const winHeight = window.innerHeight;
    const spaceBelow = winHeight - btnRect.bottom;
    const spaceAbove = btnRect.top;
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        return 'up';
    }
    return 'down';
}


const NavLink = ({to, icon, label, activeMarkClasses}) => {
    const location = useLocation();
    const isActive = location.pathname === to;

    return (
        <Tooltip title={label} placement="right" arrow>
            <Link to={to}
                  className={`flex items-center justify-center w-14 h-14 rounded-lg transition-all duration-200 ${isActive ? activeMarkClasses : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                {icon}
            </Link>
        </Tooltip>
    );
};


const FlyoutMenu = ({isOpen, onClose, triggerRef, children, widthClass = 'w-60'}) => {
    const menuRef = useRef(null);
    const [direction, setDirection] = useState('down');
    const [topPosition, setTopPosition] = useState(0);

    useLayoutEffect(() => {
        if (isOpen && triggerRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const menuHeight = 220;

            const centerPosition = triggerRect.top + (triggerRect.height / 2) - (menuHeight / 2);

            const maxTop = window.innerHeight - menuHeight - 16;
            const minTop = 16;
            const finalTop = Math.max(minTop, Math.min(centerPosition, maxTop));

            setTopPosition(finalTop);
            setDirection(getDropdownDirection(triggerRef));
        }
    }, [isOpen, triggerRef]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)
            ) {
                onClose();
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isOpen, onClose, triggerRef]);

    if (!isOpen) return null;

    return (
        <div
            ref={menuRef}
            style={{top: `${topPosition}px`}}
            className={`fixed z-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 flex flex-col gap-1 border border-gray-200 dark:border-gray-700 ${widthClass} left-[80px] animate-fade-in-fast`}
        >
            {children}
        </div>
    );
};


const SideMenu = memo(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const {theme, toggleTheme} = useTheme();
    const {userData, isLoading, hasPermission} = useAuthData();

    const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
    const [isDashboardMenuOpen, setIsDashboardMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isCredencialModalOpen, setIsCredencialModalOpen] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [openSubcategory, setOpenSubcategory] = useState(null);
    const [submenuPosition, setSubmenuPosition] = useState({top: 0, left: 0});
    const closeTimeoutRef = useRef(null);

    const settingsButtonRef = useRef(null);
    const dashboardButtonRef = useRef(null);
    const profileButtonRef = useRef(null);
    const helpButtonRef = useRef(null);

    const is_primeiro_login = JSON.parse(localStorage.getItem('is_primeiro_login') || 'false');
    const login_method = localStorage.getItem('login_method');
    const userType = useUserType();
    const isAdmin = userType === 'administrador';

    const token = localStorage.getItem("token");
    const {usuario_id: userId, nome: nomeUsuario = "Usuário"} = token ? jwtDecode(token) : {};
    const {
        dashboards, categories, loading, error, handleToggleFavorite, fetchDashboards, getFavoriteDashboards
    } = useDashboards(userId);

    useEffect(() => {
        if (error) {
            console.error('Erro ao carregar dashboards:', error);
        }
        if (!loading) {
            console.log('Categories carregadas:', categories);
        }
    }, [categories, loading, error]);

    const activeMarkClasses = theme === 'dark'
        ? 'bg-white text-[#003B71]'
        : 'bg-[#003B71] text-white';

    useEffect(() => {
        if (is_primeiro_login === true) setIsPasswordModalOpen(true);
    }, [is_primeiro_login]);

    useEffect(() => {
        setIsSettingsMenuOpen(false);
        setIsProfileMenuOpen(false);
        setIsDashboardMenuOpen(false);
        setOpenSubcategory(null);
    }, [location.pathname]);

    const handleProfileMenuClick = (key) => {
        setIsProfileMenuOpen(false);
        switch (key) {
            case 'credencial':
                setIsCredencialModalOpen(true);
                break;
            case 'senha':
                setIsPasswordModalOpen(true);
                break;
            case 'logout':
                localStorage.clear();
                navigate('/login');
                break;
            default:
                break;
        }
    };

    const handleSubcategoryHover = (subData, event) => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setOpenSubcategory(subData.id);
        const rect = event.currentTarget.getBoundingClientRect();
        setSubmenuPosition({
            top: rect.top,
            left: rect.right + 8
        });
    };

    const handleSubcategoryLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setOpenSubcategory(null);
        }, 200);
    };

    const handleSubmenuEnter = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const handleSubmenuLeave = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setOpenSubcategory(null);
        }, 200);
    };

    const {menuItems} = useMenuControl();
    const settingsItem = menuItems.find(item => item.key === '2');
    const settingsChildren = settingsItem ? settingsItem.children : [];

    function normalize(str) {
        if (!str) return "";
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim()
            .toLowerCase();
    }

    return (
        <>
            <aside
                className="w-20 h-screen fixed top-0 left-0 hidden md:flex flex-col items-center shadow-[4px_0_15px_-2px_rgba(0,0,0,0.25)] dark:shadow-[4px_0_15px_-2px_rgba(255,255,255,0.1)] z-50 bg-white dark:bg-gray-900 transition-colors duration-300">
                <div className="flex items-center justify-center h-20 shrink-0">
                    <Link to="/inicio">
                        <img src="/logo1.png" alt="Logo" className="object-contain"/>
                    </Link>
                </div>

                <div className="flex flex-col items-center justify-center flex-grow w-full">
                    <nav className="flex flex-col items-center gap-4">
                        <NavLink to="/inicio" label="Página Inicial" icon={<Home/>}
                                 activeMarkClasses={activeMarkClasses}/>
                        <Tooltip title="Dashboards" placement="right" arrow>
                            <button ref={dashboardButtonRef}
                                    onClick={() => setIsDashboardMenuOpen(!isDashboardMenuOpen)}
                                    className={`flex items-center justify-center w-14 h-14 rounded-lg transition-colors duration-200 ${isDashboardMenuOpen ? activeMarkClasses : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                                <DashboardCustomizeIcon/>
                            </button>
                        </Tooltip>
                        {isAdmin && (<Tooltip title="Configurações" placement="right" arrow>
                            <button ref={settingsButtonRef} onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
                                    className={`flex items-center justify-center w-14 h-14 rounded-lg transition-colors duration-200 ${isSettingsMenuOpen ? activeMarkClasses : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                                <Settings/>
                            </button>
                        </Tooltip>)}
                    </nav>
                </div>

                <div className="flex flex-col items-center gap-4 mb-4">
                    <Tooltip title="Ajuda" placement="right" arrow>
                        <IconButton ref={helpButtonRef} onClick={() => setIsHelpModalOpen(!isHelpModalOpen)}
                                    aria-label="Ajuda">
                            <HelpOutlineIcon/>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Meu Perfil" placement="right" arrow>
                        <IconButton ref={profileButtonRef} onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}>
                            <Avatar className="h-10 w-10 bg-[#003B71] text-white font-semibold">
                                {isLoading ?
                                    <Spin size="small"/> : userData?.nome ? userData.nome.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </div>
            </aside>

            <FlyoutMenu isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} triggerRef={helpButtonRef}
                        widthClass="w-96">
                <div className="p-4">
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                        <HelpOutlineIcon className="text-blue-600 dark:text-blue-400"/>
                        <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-100">Ajuda</h3>
                    </div>

                    <div className="space-y-4">
                        <div className="flex gap-3">
                            <div
                                className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 text-sm font-semibold">
                                1
                            </div>
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    Para acessar a documentação completa em PDF, visite o <a href={"#"}>link</a>.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <div
                                className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 text-sm font-semibold">
                                2
                            </div>
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    Para acessar a documentação em vídeo, visite o <a href={"#"}>link</a>.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </FlyoutMenu>

            <FlyoutMenu isOpen={isSettingsMenuOpen} onClose={() => setIsSettingsMenuOpen(false)}
                        triggerRef={settingsButtonRef}>
                {settingsChildren.map(child => (
                    <Link key={child.key} to={child.path} onClick={() => setIsSettingsMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                        {child.icon}
                        <span>{child.label}</span>
                    </Link>
                ))}
            </FlyoutMenu>

            <FlyoutMenu
                isOpen={isDashboardMenuOpen}
                onClose={() => {
                    setIsDashboardMenuOpen(false);
                    setOpenSubcategory(null);
                }}
                triggerRef={dashboardButtonRef}
                widthClass="w-72"
            >
                {loading ? (
                    <div className="flex items-center justify-center p-4">
                        <Spin/>
                    </div>
                ) : error ? (
                    <div className="px-4 py-2 text-red-500 text-sm">
                        Erro ao carregar dashboards
                    </div>
                ) : !categories || categories.length === 0 ? (
                    <div className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm">
                        Nenhum dashboard disponível
                    </div>
                ) : (
                    <div className="max-h-[40vh] overflow-y-auto">
                        {categories.sort((a, b) => {
                            const nomeA = normalize(a.name);
                            const nomeB = normalize(b.name);

                            if (nomeA === 'gestao') return -1;
                            if (nomeB === 'gestao') return 1;

                            return nomeA.localeCompare(nomeB);
                        }).map((category) => (
                            <div key={category.name} className="mb-3 last:mb-0 font-medium">
                                <div
                                    className="px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-400 uppercase tracking-wider">
                                    {category.name}
                                </div>
                                <div className="space-y-1">
                                    {Object.entries(category.subcategories).map(([subName, subData]) => (
                                        <div
                                            key={subData.id}
                                            onMouseEnter={(e) => handleSubcategoryHover(subData, e)}
                                            onMouseLeave={handleSubcategoryLeave}
                                        >
                                            <div
                                                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors cursor-pointer flex items-center justify-between">
                                                <span>{subName}</span>
                                                <span className="text-xs">›</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </FlyoutMenu>

            {openSubcategory && categories.map((category) =>
                Object.entries(category.subcategories).map(([subName, subData]) =>
                        openSubcategory === subData.id && subData.dashboards.length > 0 && (
                            <div
                                key={`submenu-${subData.id}`}
                                className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 min-w-[250px] max-w-[300px] z-50 py-1"
                                style={{
                                    top: `${submenuPosition.top}px`,
                                    left: `${submenuPosition.left}px`,
                                    pointerEvents: 'auto'
                                }}
                                onMouseEnter={handleSubmenuEnter}
                                onMouseLeave={handleSubmenuLeave}
                            >
                                {subData.dashboards.map((dashboard) => (
                                    <Link
                                        key={dashboard.id}
                                        to={dashboard.path}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (closeTimeoutRef.current) {
                                                clearTimeout(closeTimeoutRef.current);
                                            }
                                            setIsDashboardMenuOpen(false);
                                            setOpenSubcategory(null);
                                        }}
                                        className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <DashboardIcon className="text-gray-500 dark:text-gray-400 flex-shrink-0"
                                                       fontSize="small"/>
                                        <span className="flex-1 text-sm">{dashboard.label}</span>
                                        {dashboard.is_favorito && (
                                            <Bookmarks className="text-yellow-500 flex-shrink-0" fontSize="small"/>
                                        )}
                                    </Link>
                                ))}
                            </div>
                        )
                )
            )}

            <FlyoutMenu isOpen={isProfileMenuOpen} onClose={() => setIsProfileMenuOpen(false)}
                        triggerRef={profileButtonRef}>
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 mb-1">
                    <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">{isLoading ?
                        <Spin size="small"/> : userData?.nome || 'Usuário'}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{userData?.email}</p>
                </div>
                {hasPermission('administrador') && (
                    <button onClick={() => handleProfileMenuClick('credencial')}
                            className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors text-left w-full">
                        <UserOutlined/><span>Editar Credencial</span>
                    </button>
                )}
                {login_method === '0' && (
                    <button onClick={() => handleProfileMenuClick('senha')}
                            className="flex items-center gap-3 px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors text-left w-full">
                        <LockOutlined/><span>Alterar Senha</span>
                    </button>
                )}
                <button onClick={() => handleProfileMenuClick('logout')}
                        className="flex items-center gap-3 px-4 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-gray-700 rounded-md transition-colors text-left w-full">
                    <LogoutOutlined/><span>Sair</span>
                </button>
            </FlyoutMenu>

            {login_method === '0' && <ChangePasswordModal open={isPasswordModalOpen}
                                                          onClose={!is_primeiro_login ? () => setIsPasswordModalOpen(false) : undefined}
                                                          onSuccess={() => setIsPasswordModalOpen(false)}/>}
            <EditarCredencial open={isCredencialModalOpen} onClose={() => setIsCredencialModalOpen(false)}
                              onSuccess={() => setIsCredencialModalOpen(false)}/>
        </>
    );
});

export default SideMenu;